import { Box, styled, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import CheckIcon from '@mui/icons-material/Check';

const PREFIX = 'PasswordRequirements';

const classes = {
  requirements: `${PREFIX}-requirements`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.requirements}`]: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    alignSelf: 'stretch',
    padding: `${theme.spacing(2)} ${theme.spacing(3)}`,
    backgroundColor: theme.palette.grey[100],
    mixBlendMode: 'multiply',
    borderBottomLeftRadius: theme.shape.borderRadius,
    borderBottomRightRadius: theme.shape.borderRadius,
  },
}));

const RequirementItem = ({
  label,
  passed,
}: {
  label: string;
  passed: boolean;
}) => (
  <Box display="flex" alignItems="center" gap={1}>
    <Typography
      component="span"
      variant="body2"
      color={passed ? 'success.main' : 'lightTextPrimary.main'}>
      {passed ? <CheckIcon color="success" fontSize="small" /> : '—'}
    </Typography>
    <Typography
      component="span"
      variant="body2"
      color={passed ? 'success.main' : 'lightTextPrimary.main'}>
      {label}
    </Typography>
  </Box>
);

export default function PasswordRequirements({
  password,
}: {
  password: string;
}) {
  const { t } = useTranslation('member');
  const passwordChecks = {
    lowercase: /[a-z]/.test(password),
    uppercase: /[A-Z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[!@#$%.]/.test(password),
    length: password.length >= 8,
  };

  return (
    <StyledBox className={classes.requirements}>
      <Typography variant="body1">
        {t('member.passwordStrength.requirements')}:
      </Typography>
      <RequirementItem
        label={t('member.passwordStrength.lowercaseLetter')}
        passed={passwordChecks.lowercase}
      />
      <RequirementItem
        label={t('member.passwordStrength.uppercaseLetter')}
        passed={passwordChecks.uppercase}
      />
      <RequirementItem
        label={t('member.passwordStrength.number')}
        passed={passwordChecks.number}
      />
      <RequirementItem
        label={t('member.passwordStrength.specialCharacter')}
        passed={passwordChecks.special}
      />
      <RequirementItem
        label={t('member.passwordStrength.minCharacters')}
        passed={passwordChecks.length}
      />
    </StyledBox>
  );
}
